import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '@/contexts/AuthContext';

const API_PET = `${API_URL}/pets/me`;
const CONSULTA_MS = 45000;
const PERFIS_COM_PET = ['comercial', 'master', 'administrativo'];
const PERFIS_COM_OVO_NA_ENTRADA = ['comercial', 'administrativo']; // o master liga pelo menu

// Estado do pet de vendas de quem está logado (spec 2026-10-02, seções 3.1 e 5.5).
// Falhas são silenciosas: o pet nunca atrapalha o ERP, e a próxima consulta tenta de novo.
export function usePet(user) {
  const papel = user?.role;
  const pode = PERFIS_COM_PET.includes(papel);
  const [estado, setEstado] = useState(null);
  const [recemNascido, setRecemNascido] = useState(false);
  const vistoPendente = useRef(null);
  // Sobe a cada gravação: uma consulta que saiu antes dela e chegou depois traria o pet de antes
  // (por exemplo, o visual antigo logo depois de o master escolher outro).
  const versao = useRef(0);

  const alterar = useCallback(async (campos) => {
    versao.current += 1;
    const { data } = await axios.patch(API_PET, campos);
    setEstado(data);
    return data;
  }, []);

  // Se gravar o visto_ate falhar, a próxima consulta que der certo grava de novo (sem repetir a animação).
  const marcarVisto = useCallback(async (ate) => {
    try {
      return await alterar({ visto_ate: ate });
    } catch {
      vistoPendente.current = ate;
      return null;
    }
  }, [alterar]);

  const buscar = useCallback(async () => {
    const dessaConsulta = versao.current;
    try {
      const { data } = await axios.get(API_PET);
      if (dessaConsulta === versao.current) setEstado(data); // senão uma gravação passou na frente: a resposta dela é a mais nova
      if (vistoPendente.current) {
        const ate = vistoPendente.current;
        vistoPendente.current = null;
        marcarVisto(ate);
      }
      return data;
    } catch {
      return null;
    }
  }, [marcarVisto]);

  const chocar = useCallback(async () => {
    versao.current += 1;
    try {
      const { data } = await axios.post(API_PET);
      setEstado(data);
      // um pet que já existia e está escondido não choca de novo (o pet novo sempre nasce visível)
      setRecemNascido(Boolean(data.visivel));
    } catch {
      // sem pet desta vez; a opção do menu continua lá
    }
  }, []);

  // Ao entrar: busca o pet; vendedor e administrativo sem pet ganham o ovo na hora (master liga pelo menu).
  useEffect(() => {
    if (!pode) {
      setEstado(null);
      return undefined;
    }
    let ativo = true;
    buscar().then((dados) => {
      if (ativo && dados && !dados.existe && PERFIS_COM_OVO_NA_ENTRADA.includes(papel)) chocar();
    });
    return () => {
      ativo = false;
    };
  }, [pode, papel, buscar, chocar]);

  // Com o pet na tela: consulta a cada 45 s e assim que a aba volta a ficar visível.
  const visivel = Boolean(estado?.existe && estado.visivel);
  useEffect(() => {
    if (!visivel) return undefined;
    const consultar = () => {
      if (!document.hidden) buscar();
    };
    const id = setInterval(consultar, CONSULTA_MS);
    document.addEventListener('visibilitychange', consultar);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', consultar);
    };
  }, [visivel, buscar]);

  const existe = Boolean(estado?.existe);
  const mostrar = useCallback(async (ligar) => {
    // esconder antes de responder ao balão não pode repetir o ovo quando o pet voltar
    if (!ligar) setRecemNascido(false);
    if (ligar && !existe) {
      await chocar();
      return;
    }
    try {
      await alterar({ visivel: ligar });
    } catch {
      // fica como estava; o menu mostra o que o servidor tem
    }
  }, [existe, chocar, alterar]);

  const escolherNome = useCallback((nome) => alterar({ nome }), [alterar]);
  // só o Master; para os outros perfis o servidor responde 403
  const escolherPet = useCallback((semente) => alterar({ semente }), [alterar]);
  const fimDoNascimento = useCallback(() => setRecemNascido(false), []);

  return {
    pode, estado, recemNascido, mostrar, escolherNome, escolherPet, marcarVisto, fimDoNascimento, recarregar: buscar,
  };
}
